/**
 * Thin typed wrapper over the official `telegram-web-app.js` global. Only the surface we use.
 * Outside Telegram (browser dev), every call is a safe no-op and initData comes from
 * VITE_DEV_INIT_DATA.
 */

interface TgThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
}

interface TgHaptic {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

interface TgBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; username?: string } };
  version: string;
  platform: string;
  colorScheme: "light" | "dark";
  themeParams: TgThemeParams;
  isExpanded: boolean;
  viewportStableHeight: number;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  enableClosingConfirmation(): void;
  disableVerticalSwipes?(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  shareToStory?(mediaUrl: string, params?: { text?: string }): void;
  HapticFeedback: TgHaptic;
  BackButton: TgBackButton;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

/** Matches `bg-2` in tailwind.config.ts and the body background in styles.css. */
const APP_BG = "#0b0d12";

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

const wa: TgWebApp | undefined =
  typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

/** True when running inside a real Telegram client (initData is present). */
export const insideTelegram = Boolean(wa && wa.initData);

export const tg = {
  get initData(): string {
    if (wa?.initData) return wa.initData;
    return import.meta.env.VITE_DEV_INIT_DATA ?? "";
  },
  get platform(): string {
    return wa?.platform ?? "browser";
  },
  get colorScheme(): "light" | "dark" {
    return wa?.colorScheme ?? "dark";
  },
  get startParam(): string | undefined {
    return wa?.initDataUnsafe.start_param;
  },

  /** Call once on mount: tells Telegram we are ready, expands, applies theme. */
  boot(): void {
    if (!wa) return;
    wa.ready();
    wa.expand();
    wa.disableVerticalSwipes?.();
    // The app is always dark (see tailwind.config.ts); paint Telegram's chrome to match.
    wa.setHeaderColor(APP_BG);
    wa.setBackgroundColor(APP_BG);
    wa.setBottomBarColor?.(APP_BG);
  },

  haptic: {
    light: () => wa?.HapticFeedback.impactOccurred("light"),
    medium: () => wa?.HapticFeedback.impactOccurred("medium"),
    success: () => wa?.HapticFeedback.notificationOccurred("success"),
    error: () => wa?.HapticFeedback.notificationOccurred("error"),
    select: () => wa?.HapticFeedback.selectionChanged(),
  },

  /** Open Telegram's native share sheet pre-filled with a URL and text. */
  share(url: string, text: string): void {
    const target = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    if (wa) wa.openTelegramLink(target);
    else window.open(target, "_blank", "noopener");
  },

  openLink(url: string): void {
    if (wa) wa.openLink(url);
    else window.open(url, "_blank", "noopener");
  },

  backButton: {
    show(cb: () => void): () => void {
      if (!wa) return () => {};
      wa.BackButton.onClick(cb);
      wa.BackButton.show();
      return () => {
        wa.BackButton.offClick(cb);
        wa.BackButton.hide();
      };
    },
  },

  onThemeChanged(cb: () => void): () => void {
    if (!wa) return () => {};
    wa.onEvent("themeChanged", cb);
    return () => wa.offEvent("themeChanged", cb);
  },
};
